import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useContainerWidth, useMenuOverlay, useOverlay } from '@fs/zion-ui'
import {
  artifactManagerService,
  getUserMemories,
  memoryUtils,
  reportToSentry,
} from '@fs/zion-memories/memories-authorized-use-only'
import { useEffectiveUser } from '@fs/zion-user'
import { useHistory } from '@fs/zion-router'
import ReportAbuseOverlay from '@fs/zion-report-abuse-overlay'
import { getPreference, setPreference } from '@fs/zion-preferences'
import { AlbumsProvider } from './AlbumsContext'
import MemoriesProviderWrapper from './MemoriesProviderWrapper'
import {
  convertObjectsToIds,
  filterOutMemories,
  filterOutMemory,
  getHeightFromRef,
  getPathFromUri,
  isAlbumPath,
  paths,
  removeTrailingSlash,
} from './galleryUtils'

const GalleryContext = createContext()
const GalleryApiContext = createContext()

const galleryActionTypes = {
  beginEditing: 'BEGIN_EDITING',
  endEditing: 'END_EDITING',
  init: 'INIT',
  removeMemories: 'REMOVE_MEMORIES',
  setActivePage: 'SET_ACTIVE_PAGE',
  setArchivedMemories: 'SET_ARCHIVED_MEMORIES',
  setBookmarkedMemories: 'SET_BOOKMARKED_MEMORIES',
  setDisplayType: 'SET_DISPLAY_TYPE',
  setFieldToFocus: 'SET_FIELD_TO_FOCUS',
  setMemoriesDirty: 'SET_MEMORIES_DIRTY',
  setMemoriesLoading: 'SET_MEMORIES_LOADING',
  setPendingAction: 'SET_PENDING_ACTION',
  setRecentlyDeletedMemories: 'SET_RECENTLY_DELETED_MEMORIES',
  setRestoredMemories: 'SET_RESTORED_MEMORIES',
  setSelectedIds: 'SET_SELECTED_IDS',
  setSelectMode: 'SET_SELECT_MODE',
  setShowExplorer: 'SET_SHOW_EXPLORER',
  setTombstones: 'SET_TOMBSTONES',
  setUserMemories: 'SET_USER_MEMORIES',
  updateMemories: 'UPDATE_MEMORIES',
  updateMemory: 'UPDATE_MEMORY',
}

const collections = {
  albums: 'albums',
  archive: 'archive',
  bookmarkedAlbums: 'bookmarkedAlbums',
  bookmarks: 'bookmarks',
  deleted: 'deleted',
  memories: 'memories',
  missing: 'missing',
}

const displayTypes = {
  grid: 'grid',
  list: 'list',
}

const galleryColumnKeys = {
  MEMORY_IMG: 'column1',
  TITLE: 'column2',
  PLACE: 'column3',
  DATE_OF_MEMORY: 'column4',
  DATE_ADDED: 'column5',
  VISIBILITY: 'column6',
}

const isMemoryError = (index, memoriesResponses) =>
  memoriesResponses?.[index]?.status === 'rejected' || memoriesResponses?.[index]?.value?.memoryError

const replaceMemory = (updatedMemory, memories) => {
  const memoryToReplace = memories.findIndex((memory) => memory?.id === updatedMemory?.id)
  const updatedMemories = [...memories]
  updatedMemories[memoryToReplace] = updatedMemory
  return updatedMemories
}

const galleryReducer = (state, action) => {
  switch (action.type) {
    case galleryActionTypes.init:
      return {
        ...state,
        archivedMemories: action.archivedMemories,
        bookmarkedMemories: action.bookmarkedMemories,
        missingMemories: action.missingMemories,
        recentlyDeletedMemories: action.recentlyDeletedMemories,
        tombstones: action.tombstones,
        userMemories: action.userMemories,
        memoriesErrors: action.memoriesErrors,
        memoriesLoading: false,
      }
    case galleryActionTypes.addMemories: {
      return { ...state, userMemories: [...state.userMemories, ...action.memories] }
    }
    case galleryActionTypes.removeMemories: {
      const recentlyDeletedMemories = [...state.recentlyDeletedMemories, ...action.memories]
      const deletedIds = convertObjectsToIds(action.memories)
      const bookmarkedMemories = filterOutMemories(state.bookmarkedMemories, deletedIds)
      const newState = { ...state, bookmarkedMemories, recentlyDeletedMemories }

      if (action.memories?.[0]?.archived) {
        return {
          ...newState,
          archivedMemories: filterOutMemories(state.archivedMemories, deletedIds),
          memoriesDirty: true,
        }
      }

      return {
        ...newState,
        userMemories: filterOutMemories(state.userMemories, deletedIds),
        memoriesDirty: true,
      }
    }
    case galleryActionTypes.setShowExplorer:
      return { ...state, showExplorer: action.showExplorer }
    case galleryActionTypes.setMemoriesLoading:
      return { ...state, memoriesLoading: action.memoriesLoading }
    case galleryActionTypes.setMemoriesDirty:
      return { ...state, memoriesDirty: action.memoriesDirty }
    case galleryActionTypes.setDisplayType: {
      return { ...state, displayType: action.displayType }
    }
    case galleryActionTypes.setFieldToFocus:
      return { ...state, fieldToFocus: action.fieldToFocus }
    case galleryActionTypes.setSelectMode:
      return {
        ...state,
        pendingAction: false,
        selectMode: action.selectMode,
        selectedIds: action.selectMode ? state.selectedIds : [],
      }
    case galleryActionTypes.setSelectedIds:
      return { ...state, selectedIds: action.selectedIds }
    case galleryActionTypes.setActivePage: {
      const { path } = action
      let activePage = getPathFromUri(path)

      if (isAlbumPath(path)) {
        activePage = paths.album
      }

      activePage = removeTrailingSlash(activePage)
      return { ...state, activePage }
    }
    case galleryActionTypes.setArchivedMemories:
      return { ...state, archivedMemories: action.archivedMemories }
    case galleryActionTypes.setBookmarkedMemories:
      return { ...state, bookmarkedMemories: action.bookmarkedMemories }
    case galleryActionTypes.setPendingAction:
      return { ...state, pendingAction: action.pendingAction }
    case galleryActionTypes.setRecentlyDeletedMemories:
      return { ...state, recentlyDeletedMemories: action.recentlyDeletedMemories }
    case galleryActionTypes.setRestoredMemories:
      return {
        ...state,
        tombstones: action.tombstones,
        recentlyDeletedMemories: action.recentlyDeletedMemories,
        userMemories: [...state.userMemories, ...action.userMemories],
      }
    case galleryActionTypes.setTombstones:
      return { ...state, tombstones: action.tombstones }
    case galleryActionTypes.setUserMemories:
      return { ...state, userMemories: action.userMemories }
    case galleryActionTypes.beginEditing: {
      state.actionsMenuOverlay.close()
      return { ...state, editing: true }
    }
    case galleryActionTypes.endEditing:
      return { ...state, editing: false }
    case galleryActionTypes.updateMemory:
      return {
        ...state,
        archivedMemories: replaceMemory(action.memory, state.archivedMemories),
        bookmarkedMemories: replaceMemory(action.memory, state.bookmarkedMemories),
        recentlyDeletedMemories: replaceMemory(action.memory, state.recentlyDeletedMemories),
        userMemories: replaceMemory(action.memory, state.userMemories),
      }
    case galleryActionTypes.updateMemories: {
      // Create a map of updated memories by ID for O(1) lookup
      const updatedMemoriesMap = new Map(action.memories.map((memory) => [memory.id, memory]))

      // Helper function to efficiently replace multiple memories in a single pass
      const replaceMultipleMemories = (memories) => {
        if (!memories || memories.length === 0) return memories

        let hasChanges = false
        const updated = memories.map((memory) => {
          const updatedMemory = updatedMemoriesMap.get(memory?.id)
          if (updatedMemory) {
            hasChanges = true
            return updatedMemory
          }
          return memory
        })

        return hasChanges ? updated : memories
      }

      return {
        ...state,
        archivedMemories: replaceMultipleMemories(state.archivedMemories),
        bookmarkedMemories: replaceMultipleMemories(state.bookmarkedMemories),
        recentlyDeletedMemories: replaceMultipleMemories(state.recentlyDeletedMemories),
        userMemories: replaceMultipleMemories(state.userMemories),
      }
    }
    default:
      return state
  }
}

const GalleryProvider = ({ children }) => {
  const isCurrent = useRef(true)
  const history = useHistory()
  const { cisId, userLoading } = useEffectiveUser()
  const atSize = useContainerWidth()
  const isMobile = atSize({ default: true, xl: false })
  const actionsMenuOverlay = useMenuOverlay()
  const contextMenuOverlay = useMenuOverlay()
  const actionsBarRef = useRef()
  const mobileFilterBarRef = useRef()
  const addToAlbumOverlay = useOverlay()
  const deleteMemoryOverlay = useOverlay()
  const makePrivateOverlay = useOverlay()
  const makePublicOverlay = useOverlay()
  const multiDownloadOverlay = useOverlay()
  const topicTagsOverlay = useOverlay()
  const permanentDeleteOverlay = useOverlay()
  const reportAbuseOverlay = useOverlay()
  const editorOverlay = useOverlay()
  const isInitialLoadRef = useRef(true)
  const { getAllMissingMemories, getRecentlyDeletedMemories } = artifactManagerService

  const initialState = useMemo(
    () => ({
      actionsMenuOverlay,
      activePage: paths.gallery,
      archivedMemories: [],
      bookmarkedMemories: [],
      displayType: displayTypes.grid,
      editing: false,
      fieldToFocus: null,
      memoriesDirty: false,
      memoriesErrors: [],
      memoriesLoading: true,
      missingMemories: [],
      pendingAction: false,
      recentlyDeletedMemories: [],
      selectedIds: [],
      selectMode: false,
      showExplorer: !isMobile,
      tombstones: [],
      userMemories: [],
    }),
    [actionsMenuOverlay, isMobile]
  )

  const [state, dispatch] = useReducer(galleryReducer, initialState)
  const selectedIdsCount = useMemo(() => state.selectedIds?.length ?? 0, [state.selectedIds])

  const getFilteredMemories = useCallback(
    (memories) => memories.filter((memory) => state.selectedIds.includes(memory.id)),
    [state.selectedIds]
  )

  const selectedMemories = useMemo(() => {
    const allMemories = [...state.userMemories, ...state.archivedMemories, ...state.bookmarkedMemories]

    let filteredMemories = getFilteredMemories(allMemories)
    if (filteredMemories.length === 0) {
      filteredMemories = getFilteredMemories(state.recentlyDeletedMemories)
    }

    const uniqueMemories = Array.from(new Set(filteredMemories.map((memory) => memory.id))).map((id) =>
      filteredMemories.find((memory) => memory.id === id)
    )

    return uniqueMemories
  }, [
    getFilteredMemories,
    state.archivedMemories,
    state.bookmarkedMemories,
    state.recentlyDeletedMemories,
    state.userMemories,
  ])

  const activeMemories = useMemo(() => {
    switch (state.activePage) {
      case paths.archive:
        return state.archivedMemories
      case paths.recentlyDeleted:
        return state.recentlyDeletedMemories
      case paths.bookmarks:
        return state.bookmarkedMemories
      // This needs to return an empty array to check for when to show the Image for empty collections in MemoriesContent
      // If you need the memories from an album, use albumMemories from useAlbums
      case paths.album:
        return []
      default:
        return state.userMemories
    }
  }, [
    state.activePage,
    state.archivedMemories,
    state.bookmarkedMemories,
    state.recentlyDeletedMemories,
    state.userMemories,
  ])

  const actionsBarHeight = getHeightFromRef(actionsBarRef)
  const mobileFilterBarHeight = getHeightFromRef(mobileFilterBarRef)

  const galleryActions = useMemo(
    () => ({
      init: ({
        archivedMemories,
        bookmarkedMemories,
        recentlyDeletedMemories,
        tombstones,
        userMemories,
        missingMemories,
        memoriesErrors,
      }) =>
        dispatch({
          type: galleryActionTypes.init,
          archivedMemories,
          bookmarkedMemories,
          recentlyDeletedMemories,
          tombstones,
          userMemories,
          missingMemories,
          memoriesErrors,
        }),
      addMemories: ({ memories }) => dispatch({ type: galleryActionTypes.addMemories, memories }),
      removeMemories: ({ memories }) => dispatch({ type: galleryActionTypes.removeMemories, memories }),
      setShowExplorer: (showExplorer) => dispatch({ type: galleryActionTypes.setShowExplorer, showExplorer }),
      setMemoriesLoading: (memoriesLoading) =>
        dispatch({ type: galleryActionTypes.setMemoriesLoading, memoriesLoading }),
      setActivePage: (path) => dispatch({ type: galleryActionTypes.setActivePage, path }),
      setDisplayType: (displayType) => dispatch({ type: galleryActionTypes.setDisplayType, displayType }),
      setFieldToFocus: (fieldToFocus) => dispatch({ type: galleryActionTypes.setFieldToFocus, fieldToFocus }),
      setMemoriesDirty: (memoriesDirty) => dispatch({ type: galleryActionTypes.setMemoriesDirty, memoriesDirty }),
      setSelectMode: (selectMode) => dispatch({ type: galleryActionTypes.setSelectMode, selectMode }),
      setSelectedIds: (selectedIds) => dispatch({ type: galleryActionTypes.setSelectedIds, selectedIds }),
      setArchivedMemories: (archivedMemories) =>
        dispatch({ type: galleryActionTypes.setArchivedMemories, archivedMemories }),
      setBookmarkedMemories: (bookmarkedMemories) =>
        dispatch({ type: galleryActionTypes.setBookmarkedMemories, bookmarkedMemories }),
      setPendingAction: (pendingAction) => dispatch({ type: galleryActionTypes.setPendingAction, pendingAction }),
      setRecentlyDeletedMemories: (recentlyDeletedMemories) =>
        dispatch({ type: galleryActionTypes.setRecentlyDeletedMemories, recentlyDeletedMemories }),
      setRestoredMemories: ({ tombstones, recentlyDeletedMemories, userMemories }) =>
        dispatch({
          type: galleryActionTypes.setRestoredMemories,
          tombstones,
          recentlyDeletedMemories,
          userMemories,
        }),
      setTombstones: (tombstones) => dispatch({ type: galleryActionTypes.setTombstones, tombstones }),
      setUserMemories: (userMemories) => dispatch({ type: galleryActionTypes.setUserMemories, userMemories }),
      updateMemories: (memories) => dispatch({ type: galleryActionTypes.updateMemories, memories }),
      updateMemory: (memory) => dispatch({ type: galleryActionTypes.updateMemory, memory }),
      beginEditing: () => dispatch({ type: galleryActionTypes.beginEditing }),
      endEditing: () => dispatch({ type: galleryActionTypes.endEditing }),
    }),
    []
  )

  const isMyMemoriesPage = useMemo(() => state.activePage === paths.gallery, [state.activePage])
  const isAlbumPage = useMemo(() => state.activePage === paths.album, [state.activePage])

  const galleryCallbacks = useMemo(
    () => ({
      archiveCallback: ({ archived, memory }) => {
        const memories = (Array.isArray(memory) ? memory : [memory]).map((m) => ({ ...m, archived: !archived }))
        const archivedIds = convertObjectsToIds(memories)
        if (archived) {
          galleryActions.setArchivedMemories(filterOutMemories(state.archivedMemories, archivedIds))
          galleryActions.setUserMemories([...state.userMemories, ...memories])
        } else {
          galleryActions.setArchivedMemories([...state.archivedMemories, ...memories])
          galleryActions.setUserMemories(filterOutMemories(state.userMemories, archivedIds))
        }
        galleryActions.setMemoriesDirty(true)
      },
      bookmarkCallback: ({ isBookmarked, memory }) => {
        if (isBookmarked) {
          galleryActions.setBookmarkedMemories(filterOutMemory(state.bookmarkedMemories, memory?.id))
        } else {
          galleryActions.setBookmarkedMemories([...state.bookmarkedMemories, memory])
        }
      },
      restoreCallback: ({ memory }) => {
        const memories = Array.isArray(memory) ? memory : [memory]
        const restoredIds = convertObjectsToIds(memories)
        // Set editableByCaller to true in state to show correct actions for restored memories without refreshing the page
        const updatedMemories = memories.map((memoryToRestore) => ({ ...memoryToRestore, editableByCaller: true }))
        // Remove tombstones from tombstones array
        const newTombstones = state.tombstones
        restoredIds.forEach((id) => delete newTombstones[id])
        galleryActions.setRestoredMemories({
          tombstones: newTombstones,
          recentlyDeletedMemories: filterOutMemories(state.recentlyDeletedMemories, restoredIds),
          userMemories: updatedMemories,
        })
      },
      expungeCallback: ({ memory }) => {
        const memories = Array.isArray(memory) ? memory : [memory]
        const expungedIds = convertObjectsToIds(memories)
        galleryActions.setRecentlyDeletedMemories(filterOutMemories(state.recentlyDeletedMemories, expungedIds))
      },
    }),
    [
      galleryActions,
      state.archivedMemories,
      state.bookmarkedMemories,
      state.recentlyDeletedMemories,
      state.tombstones,
      state.userMemories,
    ]
  )

  const fetchMemories = useCallback(async (apiParams) => {
    let errorItem = collections.memories
    let memories = []
    let memoryError = null

    if (apiParams?.archived) {
      errorItem = collections.archive
    } else if (apiParams?.bookmarked) {
      errorItem = collections.bookmarks
    }

    try {
      memories = (await getUserMemories(apiParams)) ?? []
    } catch (error) {
      reportToSentry(error, { operation: 'fetchMemories', collection: errorItem })
      memoryError = errorItem
    }
    return { memories, memoryError }
  }, [])

  const fetchRecentlyDeletedMemories = useCallback(async () => {
    let memories = []
    let memoryError = null
    let tombstones = {}
    try {
      const tombstoneData = (await getRecentlyDeletedMemories({ cisId }))?.data?.tombstones ?? []
      memories = tombstoneData.map((tombstone) => {
        const memory = tombstone?.artifactList?.[0] ?? {}
        memory.tombstoneId = tombstone?.id
        return memory
      })
      tombstones = tombstoneData.reduce((acc, memory) => {
        if (memory?.artifactList?.[0]?.id) {
          acc[memory?.artifactList?.[0]?.id] = memoryUtils.daysUntilDeleted(memory?.deletionDate)
        }
        return acc
      }, {})
    } catch (error) {
      reportToSentry(error, { operation: 'fetchRecentlyDeletedMemories', collection: collections.deleted })
      memoryError = collections.deleted
    }
    return { memories, memoryError, tombstones }
  }, [cisId, getRecentlyDeletedMemories])

  const fetchMissingMemories = useCallback(async () => {
    let memories = []
    let memoryError = null
    try {
      memories = (await getAllMissingMemories())?.data?.missing ?? []
    } catch (error) {
      reportToSentry(error, { operation: 'fetchMissingMemories', collection: collections.missing })
      memoryError = collections.missing
    }
    return { memories, memoryError }
  }, [getAllMissingMemories])

  useEffect(() => {
    const loadUserMemories = async () => {
      if (userLoading) return

      if (cisId) {
        galleryActions.setMemoriesLoading(true)

        const memoryCollectionTypes = {
          archivedMemories: 0,
          bookmarkedMemories: 1,
          userMemories: 2,
          missingMemories: 3,
          recentlyDeletedMemories: 4,
        }
        const memoriesResponses = await Promise.allSettled([
          fetchMemories({
            cisId,
            archived: true,
          }),
          fetchMemories({
            cisId,
            bookmarked: true,
          }),
          fetchMemories({ cisId }),
          fetchMissingMemories(),
          fetchRecentlyDeletedMemories(),
        ])

        const memoriesErrors = []
        if (isMemoryError(memoryCollectionTypes.archivedMemories, memoriesResponses))
          memoriesErrors.push(collections.archive)
        if (isMemoryError(memoryCollectionTypes.bookmarkedMemories, memoriesResponses))
          memoriesErrors.push(collections.bookmarks)
        if (isMemoryError(memoryCollectionTypes.userMemories, memoriesResponses))
          memoriesErrors.push(collections.memories)
        if (isMemoryError(memoryCollectionTypes.missingMemories, memoriesResponses))
          memoriesErrors.push(collections.missing)
        if (isMemoryError(memoryCollectionTypes.recentlyDeletedMemories, memoriesResponses))
          memoriesErrors.push(collections.deleted)

        isCurrent.current &&
          galleryActions.init({
            archivedMemories: memoriesResponses?.[memoryCollectionTypes.archivedMemories]?.value?.memories ?? [],
            bookmarkedMemories: memoriesResponses?.[memoryCollectionTypes.bookmarkedMemories]?.value?.memories ?? [],
            recentlyDeletedMemories:
              memoriesResponses?.[memoryCollectionTypes.recentlyDeletedMemories]?.value?.memories ?? [],
            tombstones: memoriesResponses?.[memoryCollectionTypes.recentlyDeletedMemories]?.value?.tombstones ?? {},
            userMemories: memoriesResponses?.[memoryCollectionTypes.userMemories]?.value?.memories ?? [],
            missingMemories: memoriesResponses?.[memoryCollectionTypes.missingMemories]?.value?.memories ?? [],
            memoriesErrors,
          })
      } else {
        galleryActions.setMemoriesLoading(false)
      }
    }
    loadUserMemories()
  }, [
    cisId,
    userLoading,
    fetchMemories,
    fetchMissingMemories,
    fetchRecentlyDeletedMemories,
    galleryActions,
    getAllMissingMemories,
    getRecentlyDeletedMemories,
  ])

  // If the user shrinks their browser we want the explorer to be initially closed so it doesn't cover things up
  useEffect(() => {
    if (isMobile) {
      galleryActions.setShowExplorer(false)
    }
  }, [galleryActions, isMobile])

  useEffect(() => {
    const loadPreference = async () => {
      try {
        const pref = await getPreference('memories.gallery-view')
        if (pref !== state.displayType) {
          if (pref === '') galleryActions.setDisplayType(displayTypes.grid)
          else galleryActions.setDisplayType(pref || displayTypes.grid)
        }
        isInitialLoadRef.current = false
      } catch (error) {
        console.error('Error loading gallery view preference:', error)
        isInitialLoadRef.current = false
      }
    }
    loadPreference()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this to run on mount
  }, [])

  useEffect(() => {
    if (isInitialLoadRef.current) return

    const savePreference = async () => {
      try {
        await setPreference('memories.gallery-view', state.displayType)
      } catch (error) {
        console.error('Error saving gallery view preference:', error)
      }
    }
    savePreference()
  }, [state.displayType])

  /**
   * This sets the initial active page appropriately if the user goes directly to a url in the gallery.
   * This is why we only need the useEffect to run on the initial page load.
   * This will also set the sorting type initially. If we are on the album page we want to default to the custom order.
   */
  useEffect(() => {
    const pathname = history.location.pathname
    if (pathname) {
      galleryActions.setActivePage(pathname)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above comment
  }, [])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  }, [])

  const apiValues = useMemo(
    () => ({ ...galleryActions, ...galleryCallbacks, fetchRecentlyDeletedMemories }),
    [galleryActions, galleryCallbacks, fetchRecentlyDeletedMemories]
  )

  const {
    activePage,
    archivedMemories,
    bookmarkedMemories,
    displayType,
    editing,
    fieldToFocus,
    memoriesDirty,
    memoriesErrors,
    memoriesLoading,
    missingMemories,
    pendingAction,
    recentlyDeletedMemories,
    selectedAlbum,
    selectedIds,
    selectMode,
    showExplorer,
    tombstones,
    userMemories,
  } = state

  const dataValues = useMemo(
    () => ({
      actionsBarHeight,
      actionsBarRef,
      actionsMenuOverlay,
      activeMemories,
      addToAlbumOverlay,
      contextMenuOverlay,
      activePage,
      archivedMemories,
      bookmarkedMemories,
      deleteMemoryOverlay,
      displayType,
      editing,
      editorOverlay,
      fieldToFocus,
      isAlbumPage,
      isMobile,
      makePrivateOverlay,
      makePublicOverlay,
      memoriesDirty,
      memoriesErrors,
      memoriesLoading,
      missingMemories,
      mobileFilterBarHeight,
      mobileFilterBarRef,
      multiDownloadOverlay,
      pendingAction,
      permanentDeleteOverlay,
      recentlyDeletedMemories,
      reportAbuseOverlay,
      selectedIds,
      selectedIdsCount,
      selectedMemories,
      selectMode,
      showExplorer,
      tombstones,
      topicTagsOverlay,
      userMemories,
    }),
    [
      actionsBarHeight,
      actionsMenuOverlay,
      activeMemories,
      addToAlbumOverlay,
      contextMenuOverlay,
      activePage,
      archivedMemories,
      bookmarkedMemories,
      deleteMemoryOverlay,
      displayType,
      editing,
      editorOverlay,
      fieldToFocus,
      isAlbumPage,
      isMobile,
      makePrivateOverlay,
      makePublicOverlay,
      memoriesDirty,
      memoriesErrors,
      memoriesLoading,
      missingMemories,
      mobileFilterBarHeight,
      multiDownloadOverlay,
      pendingAction,
      permanentDeleteOverlay,
      recentlyDeletedMemories,
      reportAbuseOverlay,
      selectedIds,
      selectedIdsCount,
      selectedMemories,
      selectMode,
      showExplorer,
      tombstones,
      topicTagsOverlay,
      userMemories,
    ]
  )

  return (
    <GalleryContext.Provider value={dataValues}>
      <GalleryApiContext.Provider value={apiValues}>
        <AlbumsProvider
          galleryActions={galleryActions}
          memoriesLoading={memoriesLoading}
          isAlbumPage={isAlbumPage}
          isMobile={isMobile}
          isMyMemoriesPage={isMyMemoriesPage}
          activePage={activePage}
        >
          <MemoriesProviderWrapper
            memoriesDirty={memoriesDirty}
            memoriesLoading={memoriesLoading}
            galleryCallbacks={galleryCallbacks}
            galleryMemories={activeMemories}
            setMemoriesDirty={galleryActions.setMemoriesDirty}
            isAlbumPage={isAlbumPage}
          >
            {children}
            {reportAbuseOverlay.isOpen && (
              <ReportAbuseOverlay
                overlay={reportAbuseOverlay}
                subject={`Abuse reported for album ${selectedAlbum?.id}`}
              />
            )}
          </MemoriesProviderWrapper>
        </AlbumsProvider>
      </GalleryApiContext.Provider>
    </GalleryContext.Provider>
  )
}

export {
  displayTypes,
  collections,
  galleryColumnKeys,
  galleryActionTypes,
  GalleryApiContext,
  GalleryContext,
  GalleryProvider,
}
